/**
 * Store Pinia des commandes clients (Composition API)
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { IClientOrder } from '../models/clientorder';
import { IClientOrderApiResponse } from '../models/api/clientOrderApiResponse';
import { IClientOrderFilter } from '../models/filters/clientOrderFilter';
import ClientOrderServices from '../services/ClientOrderService';
import signalRManager from '../helper/signalRManager';
import IPayloadStore from '../models/api/payloadStore';
import { useCenterStore } from './center.store';

/**
 * Store Pinia des commandes clients utilisant la Composition API
 */
const useClientOrderStore = defineStore('ClientOrders', () => {
    // State
    const datas = ref<IClientOrder[]>([]);
    const current = ref<IClientOrder>({} as IClientOrder);

    // Actions - Hydration
    /**
     * Hydrate ClientOrders from API response
     */
    async function hydrateClientOrders(orders: IClientOrderApiResponse[]): Promise<IClientOrder[]> {
        if (orders.length === 0) return [];

        // 1. Get center store
        const centerStore = useCenterStore();

        // 2. Load centers if needed
        if (centerStore.datas.length === 0) {
            await centerStore.load({ page: 1, pageSize: 0 });
        }

        // 3. Create Map for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));

        // 4. Hydrate all entities in one pass
        const models: IClientOrder[] = [];
        for (const order of orders) {
            const model = { ...order } as unknown as IClientOrder;

            // Link center reference (O(1) via Map)
            model.center = centerMap.get(order.centerId) ?? model.center;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getClientOrderById = computed(() => (id: number): IClientOrder | undefined => {
        return datas.value.find((item) => item.id === id);
    });

    // Actions
    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IClientOrderFilter) {
        const result = await ClientOrderServices.getAll<IClientOrderFilter, IClientOrderApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateClientOrders(result.data as IClientOrderApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id L'identifiant de l'élément à récupérer
     */
    async function get(id: number) {
        const result = await ClientOrderServices.getById<IClientOrderApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateClientOrders([result.data as IClientOrderApiResponse]);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            } else {
                datas.value.push(modelData);
            }

            current.value = modelData;
        }
        return result;
    }

    /**
     * Handle SignalR updates for ClientOrder
     */
    async function handleSignalRUpdate(payload: IPayloadStore) {
        console.log("From ClientOrderStore : MajClientOrder", payload);
        const updatedOrder = payload.data as IClientOrder;
        switch (payload.action) {
            case "Create": {
                const index = datas.value.findIndex((a) => a.id == updatedOrder?.id);
                if (index == -1)
                    datas.value.push(updatedOrder);
                break;
            }
            case "Update": {
                const updateIndex = datas.value.findIndex((a) => a.id == updatedOrder?.id);
                if (updateIndex > -1)
                    datas.value[updateIndex] = updatedOrder;
                else
                    datas.value.push(updatedOrder); // upsert : la commande peut arriver en Update si créée par le Row handler

                // Propager aux lignes de commande déjà hydratées dans clientOrderRowStore
                // (elles gardent une référence vers l'ancien objet clientOrder)
                // Import dynamique pour éviter la dépendance circulaire
                const { useClientOrderRowStore } = await import('./clientOrderRow.store');
                const clientOrderRowStore = useClientOrderRowStore();
                for (const row of clientOrderRowStore.datas) {
                    if (row.clientOrder?.id === updatedOrder?.id || (row as any).clientOrderId === updatedOrder?.id) {
                        row.clientOrder = updatedOrder;
                    }
                }
                break;
            }
            case "Delete": {
                const deleteIndex = datas.value.findIndex((a) => a.id == payload.id);
                if (deleteIndex > -1)
                    datas.value.splice(deleteIndex, 1);
                break;
            }
        }
    }

    return {
        // State
        datas,
        current,
        // Getters
        getClientOrderById,
        // Actions
        load,
        get,
        handleSignalRUpdate
    };
});

// SignalR handler registration
signalRManager.connection.on("MajClientOrder", (payload: IPayloadStore) => {
    useClientOrderStore().handleSignalRUpdate(payload);
});

export { useClientOrderStore };